import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsAPI, fundAPI, reportTemplatesAPI } from '../services/api';
import { FileText, Download, Search, BarChart3, ClipboardList, Building2, Settings2, Save, X, ChevronDown, ChevronRight, Filter, Plus, Columns3, FileSpreadsheet, FileBadge } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import SOAGeneration from '../components/fund/SOAGeneration';

const URGENCY_COLORS = {
  'Very High': 'bg-red-100 text-red-800',
  'High': 'bg-orange-100 text-orange-800',
  'Medium': 'bg-yellow-100 text-yellow-800',
  'Low': 'bg-green-100 text-green-800',
  'No': 'bg-gray-100 text-gray-600',
};

const REPORT_TYPES = [
  { id: 'coverage', label: 'Investor Coverage Report', icon: ClipboardList, description: 'Coverage details with interactions' },
  { id: 'coverage_plan', label: 'Investor Coverage Plan', icon: BarChart3, description: 'Sorted by urgency priority' },
  { id: 'fund_summary', label: 'Fund Summary Report', icon: FileText, description: 'Fund-level investor summary' },
  { id: 'amc_consolidated', label: 'AMC Consolidated Report', icon: Building2, description: 'Consolidated across all funds' },
  { id: 'custom', label: 'Custom Report Builder', icon: Settings2, description: 'Select any combination of fields' },
  { id: 'soa', label: 'Statement of Account (SOA)', icon: FileSpreadsheet, description: 'Generate PDF statements for investors' },
];

// Default columns per report type
const DEFAULT_COLUMNS = {
  coverage: ['first_name', 'last_name', 'email', 'mobile', 'investor_lead_name', 'distributor_name',
    'coverage_urgency', 'days_since_last_interaction', 'last_interaction_date', 'last_interaction_details',
    'last_interaction_next_steps', 'interaction_count', 'total_commitment'],
  coverage_plan: ['first_name', 'last_name', 'email', 'mobile', 'investor_lead_name', 'coverage_urgency',
    'days_since_last_interaction', 'last_interaction_date', 'touch_base_required'],
  fund_summary: ['account_name', 'contact_name', 'type_of_investor', 'folio_no', 'class_of_units',
    'commitment_amount', 'contribution_amount', 'distribution_amount', 'nav_value', 'nav_date'],
  custom: [],
};

const LS_KEY_PREFIX = 'report_columns_';

// ─── Column Selector ───
const ColumnSelector = ({ catalog, selected, onToggle, onToggleSection }) => {
  const [expandedSections, setExpandedSections] = useState({});
  const toggle = (k) => setExpandedSections(p => ({ ...p, [k]: !p[k] }));

  return (
    <div className="space-y-1 max-h-[50vh] overflow-y-auto" data-testid="column-selector">
      {Object.entries(catalog).map(([sectionKey, section]) => {
        const keys = section.fields.map(f => f.key);
        const allSel = keys.every(k => selected.includes(k));
        const someSel = keys.some(k => selected.includes(k));
        const open = expandedSections[sectionKey] !== false;
        return (
          <div key={sectionKey} className="border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={() => toggle(sectionKey)}>
              <div className="flex items-center gap-2">
                {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span className="text-sm font-medium text-gray-700">{section.section}</span>
                {someSel && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{keys.filter(k => selected.includes(k)).length}/{keys.length}</span>}
              </div>
              <button onClick={e => { e.stopPropagation(); onToggleSection(keys, !allSel); }} className="text-xs text-blue-600 hover:text-blue-800" data-testid={`toggle-section-${sectionKey}`}>
                {allSel ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            {open && (
              <div className="px-3 py-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                {section.fields.map(f => (
                  <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer py-0.5 hover:bg-gray-50 px-1 rounded">
                    <input type="checkbox" checked={selected.includes(f.key)} onChange={() => onToggle(f.key)} className="rounded text-blue-600 border-gray-300" data-testid={`col-check-${f.key}`} />
                    <span className="text-gray-700">{f.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Filter Panel ───
const FilterPanel = ({ filterableFields, filters, onFilterChange, onRemoveFilter, onAddFilter }) => (
  <div className="space-y-2" data-testid="filter-panel">
    {Object.entries(filters).map(([key, value], idx) => (
      <div key={idx} className="flex items-center gap-2">
        <select value={key} onChange={e => { const n = { ...filters }; delete n[key]; n[e.target.value] = value; onFilterChange(n); }} className="border rounded-lg px-2 py-1.5 text-sm w-48" data-testid={`filter-field-${idx}`}>
          {filterableFields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        <span className="text-gray-400 text-sm">contains</span>
        <input type="text" value={value} onChange={e => onFilterChange({ ...filters, [key]: e.target.value })} placeholder="Filter value..." className="border rounded-lg px-2 py-1.5 text-sm flex-1" data-testid={`filter-value-${idx}`} />
        <button onClick={() => onRemoveFilter(key)} className="text-gray-400 hover:text-red-500"><X size={16} /></button>
      </div>
    ))}
    <button onClick={onAddFilter} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800" data-testid="add-filter-btn"><Plus size={14} /> Add Filter</button>
  </div>
);

// ─── Dynamic Report Table ───
const DynamicTable = ({ columns, rows, totals, currency }) => {
  const numericKeys = new Set(['total_commitment', 'total_called', 'total_contribution', 'total_pending_receipt', 'total_distribution', 'total_nav',
    'commitment_amount', 'contribution_amount', 'distribution_amount', 'nav_value',
    'interaction_count', 'days_since_last_interaction']);
  const fmt = (val) => {
    if (val === null || val === undefined || val === '') return '-';
    if (typeof val === 'number') return val.toLocaleString('en-IN', { maximumFractionDigits: 2 });
    return String(val);
  };
  if (!columns.length) return <p className="text-gray-400 text-center py-8">No columns selected. Open the column selector to choose fields.</p>;
  return (
    <div>
      {totals && Object.keys(totals).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {Object.entries(totals).map(([k, v]) => {
            const col = columns.find(c => c.key === k);
            return (
              <div key={k} className="bg-gray-50 rounded-lg p-3 border">
                <p className="text-xs text-gray-500 font-medium">{col ? col.label : k}</p>
                <p className="text-lg font-bold text-gray-900">{currency ? `${currency} ` : ''}{fmt(v)}</p>
              </div>
            );
          })}
        </div>
      )}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200" data-testid="report-data-table">
          <thead className="bg-gray-50">
            <tr>{columns.map(c => <th key={c.key} className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase whitespace-nowrap ${numericKeys.has(c.key) ? 'text-right' : 'text-left'}`}>{c.label}</th>)}</tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((r, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                {columns.map(c => (
                  <td key={c.key} className={`px-4 py-3 text-sm whitespace-nowrap ${numericKeys.has(c.key) ? 'text-right font-mono' : 'text-gray-700'}`}>
                    {c.key === 'coverage_urgency' ? (
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${URGENCY_COLORS[r[c.key]] || URGENCY_COLORS['No']}`}>{r[c.key] || '-'}</span>
                    ) : fmt(r[c.key])}
                  </td>
                ))}
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-gray-400">No data matches your criteria</td></tr>}
          </tbody>
        </table>
        <div className="px-4 py-2 bg-gray-50 text-sm text-gray-500 border-t">{rows.length} record{rows.length !== 1 ? 's' : ''}</div>
      </div>
    </div>
  );
};

// ─── Main Component ───
const InvestorReports = () => {
  const navigate = useNavigate();
  const [activeReport, setActiveReport] = useState('coverage');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [funds, setFunds] = useState([]);
  const [selectedFundId, setSelectedFundId] = useState('');

  // Catalog & columns
  const [catalogData, setCatalogData] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filters, setFilters] = useState({});
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [customFundFilter, setCustomFundFilter] = useState('');
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');

  // Templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // AMC Consolidated (fixed structure, no column picker needed)
  const [amcData, setAmcData] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => { fundAPI.getAll().then(r => setFunds(r.data)).catch(() => {}); }, []);

  // Load catalog + templates
  useEffect(() => {
    if (!catalogData) {
      reportsAPI.getFieldCatalog().then(r => setCatalogData(r.data)).catch(console.error);
      reportTemplatesAPI.getAll().then(r => setTemplates(r.data)).catch(console.error);
    }
  }, [catalogData]);

  // When switching report type, load saved columns from localStorage or use defaults
  useEffect(() => {
    if (!catalogData) return;
    const saved = localStorage.getItem(LS_KEY_PREFIX + activeReport);
    if (saved) {
      try { setSelectedColumns(JSON.parse(saved)); } catch { setSelectedColumns(DEFAULT_COLUMNS[activeReport] || []); }
    } else {
      setSelectedColumns(DEFAULT_COLUMNS[activeReport] || []);
    }
    setFilters({});
    setData(null);
    setAmcData(null);
    setSelectedTemplateId('');
    setTemplateName('');
    setCustomFundFilter('');
  }, [activeReport, catalogData]);

  // Auto-fetch when columns change for pre-built reports (except custom which requires explicit Generate)
  useEffect(() => {
    if (!catalogData || !selectedColumns.length) return;
    if (activeReport === 'custom') return;
    if (activeReport === 'amc_consolidated') { fetchAMC(); return; }
    if (activeReport === 'fund_summary' && !selectedFundId) {
      if (funds.length > 0) setSelectedFundId(funds[0].id);
      return;
    }
    fetchReport();
  }, [selectedColumns, selectedFundId, activeReport, catalogData, filters]);

  // Save columns to localStorage whenever they change
  useEffect(() => {
    if (activeReport && selectedColumns.length > 0) {
      localStorage.setItem(LS_KEY_PREFIX + activeReport, JSON.stringify(selectedColumns));
    }
  }, [selectedColumns, activeReport]);

  const fetchReport = async () => {
    if (!selectedColumns.length) return;
    setLoading(true);
    setData(null);
    try {
      let res;
      if (activeReport === 'coverage') {
        res = await reportsAPI.getCustom(selectedColumns, filters, false);
      } else if (activeReport === 'coverage_plan') {
        res = await reportsAPI.getCustom(selectedColumns, filters, true);
      } else if (activeReport === 'fund_summary' && selectedFundId) {
        res = await reportsAPI.getFundSummaryCustom(selectedFundId, selectedColumns, filters);
      } else if (activeReport === 'custom') {
        res = await reportsAPI.getCustom(selectedColumns, filters, false, customFundFilter || null, customFromDate || null, customToDate || null);
      }
      if (res) setData(res.data);
    } catch (err) { console.error('Failed to fetch report:', err); }
    finally { setLoading(false); }
  };

  const fetchAMC = async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getAMCConsolidated();
      setAmcData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // Get the right catalog sections for the active report type
  const getActiveCatalog = () => {
    if (!catalogData) return {};
    const base = catalogData.contact_account || {};
    if (activeReport === 'fund_summary') {
      return { ...catalogData.fund_summary_extra, ...base };
    }
    return base;
  };

  // Column helpers
  const toggleColumn = (key) => setSelectedColumns(p => p.includes(key) ? p.filter(k => k !== key) : [...p, key]);
  const toggleSection = (keys, selectAll) => setSelectedColumns(p => { const w = p.filter(k => !keys.includes(k)); return selectAll ? [...w, ...keys] : w; });

  // Filter helpers
  const allFieldsFlat = catalogData ? [...Object.values(catalogData.contact_account || {}).flatMap(s => s.fields), ...Object.values(catalogData.fund_summary_extra || {}).flatMap(s => s.fields)] : [];
  const filterableFields = allFieldsFlat.filter(f => selectedColumns.includes(f.key));
  const addFilter = () => {
    const first = filterableFields.find(f => !(f.key in filters));
    if (first) setFilters(p => ({ ...p, [first.key]: '' }));
  };
  const removeFilter = (key) => setFilters(p => { const n = { ...p }; delete n[key]; return n; });

  // Template management
  const loadTemplate = (tpl) => {
    setSelectedColumns(tpl.columns || []);
    setFilters(tpl.filters || {});
    setSelectedTemplateId(tpl.id);
    setTemplateName(tpl.name);
  };
  const saveTemplate = async () => {
    if (!templateName.trim()) return;
    try {
      const payload = { name: templateName.trim(), columns: selectedColumns, filters };
      if (selectedTemplateId) {
        await reportTemplatesAPI.update(selectedTemplateId, payload);
      } else {
        const res = await reportTemplatesAPI.create(payload);
        setSelectedTemplateId(res.data.id);
      }
      setTemplates((await reportTemplatesAPI.getAll()).data);
      setShowSaveDialog(false);
    } catch (err) { console.error(err); }
  };
  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this saved report template?')) return;
    try {
      await reportTemplatesAPI.delete(id);
      setTemplates(p => p.filter(t => t.id !== id));
      if (selectedTemplateId === id) { setSelectedTemplateId(''); setTemplateName(''); }
    } catch (err) { console.error(err); }
  };

  // Export
  const handleExport = () => {
    let sheetData = [], fileName = 'Report';
    if (activeReport === 'amc_consolidated' && amcData) {
      sheetData = (amcData.funds || []).map(r => ({
        'Fund Name': r.fund_name, 'Currency': r.fund_currency, 'Target Size': r.target_size,
        'Investors': r.investor_count, 'Commitment': r.total_commitment, 'Contribution': r.total_contribution,
        'Distribution': r.total_distribution, 'NAV': r.total_nav,
      }));
      fileName = 'AMC_Consolidated_Report';
    } else if (data) {
      const cols = data.columns || [];
      sheetData = (data.rows || []).map(r => {
        const obj = {};
        cols.forEach(c => { obj[c.label] = r[c.key] ?? ''; });
        return obj;
      });
      const names = { coverage: 'Investor_Coverage_Report', coverage_plan: 'Investor_Coverage_Plan', fund_summary: 'Fund_Summary', custom: templateName || 'Custom_Report' };
      fileName = names[activeReport] || 'Report';
    }
    if (!sheetData.length) return;
    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${fileName}.xlsx`);
  };

  // AMC Consolidated table (fixed structure)
  const renderAMCTable = () => {
    if (!amcData) return loading ? <div className="py-12 text-center text-gray-400">Loading...</div> : null;
    const fmt = v => v != null ? Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '-';
    const rows = search ? amcData.funds.filter(r => Object.values(r).some(v => v && String(v).toLowerCase().includes(search.toLowerCase()))) : amcData.funds;
    return (
      <div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[{ l: 'Total Funds', v: amcData.total_funds }, { l: 'Grand Commitment', v: fmt(amcData.grand_totals?.commitment) },
            { l: 'Grand Contribution', v: fmt(amcData.grand_totals?.contribution) }, { l: 'Grand NAV', v: fmt(amcData.grand_totals?.nav) }].map(s => (
            <div key={s.l} className="bg-gray-50 rounded-lg p-3 border"><p className="text-xs text-gray-500 font-medium">{s.l}</p><p className="text-lg font-bold text-gray-900">{s.v}</p></div>
          ))}
        </div>
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full divide-y divide-gray-200" data-testid="amc-consolidated-report-table">
            <thead className="bg-gray-50">
              <tr>{['Fund Name', 'Currency', 'Target Size', 'Investors', 'Commitment', 'Contribution', 'Distribution', 'NAV'].map(h => <th key={h} className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">{h}</th>)}</tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{r.fund_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{r.fund_currency}</td>
                  <td className="px-4 py-3 text-sm text-right font-mono">{r.target_size ? fmt(r.target_size) : '-'}</td>
                  <td className="px-4 py-3 text-sm text-right">{r.investor_count}</td>
                  <td className="px-4 py-3 text-sm text-right font-mono">{fmt(r.total_commitment)}</td>
                  <td className="px-4 py-3 text-sm text-right font-mono">{fmt(r.total_contribution)}</td>
                  <td className="px-4 py-3 text-sm text-right font-mono">{fmt(r.total_distribution)}</td>
                  <td className="px-4 py-3 text-sm text-right font-mono">{fmt(r.total_nav)}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const showColumnPickerForReport = activeReport !== 'amc_consolidated' && activeReport !== 'soa';
  const activeCatalog = getActiveCatalog();

  return (
    <div data-testid="investor-reports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="reports-title">Reports</h1>
          <p className="text-sm text-gray-500 mt-1">Generate, customize, and export investor reports</p>
        </div>
      </div>

      {/* Report type selector */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        {REPORT_TYPES.map(rt => {
          const Icon = rt.icon;
          const active = activeReport === rt.id;
          return (
            <button key={rt.id} onClick={() => { setActiveReport(rt.id); setSearch(''); setShowColumnPicker(false); }}
              className={`flex items-start gap-3 p-3 rounded-xl border-2 transition-all text-left ${active ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'}`}
              data-testid={`report-type-${rt.id}`}>
              <div className={`p-2 rounded-lg shrink-0 ${active ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'}`}><Icon size={16} /></div>
              <div className="min-w-0"><p className={`text-sm font-semibold truncate ${active ? 'text-blue-900' : 'text-gray-700'}`}>{rt.label}</p><p className="text-xs text-gray-400 mt-0.5 truncate">{rt.description}</p></div>
            </button>
          );
        })}
      </div>

      {/* Specialised Reports */}
      <div className="mb-6">
        <h3 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Specialised Reports</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <button onClick={() => navigate('/investors/reports/capital-call-pendency')}
            className="flex items-start gap-3 p-3 rounded-xl border-2 border-amber-200 bg-amber-50/40 hover:border-amber-400 transition-all text-left"
            data-testid="link-capital-call-pendency">
            <div className="p-2 rounded-lg shrink-0 bg-amber-500 text-white"><BarChart3 size={16} /></div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900">Capital Call Pendency</p>
              <p className="text-xs text-gray-500 mt-0.5">Investors with outstanding capital calls (Called &gt; Received)</p>
            </div>
          </button>
          <button onClick={() => navigate('/investors/reports/account-transactions')}
            className="flex items-start gap-3 p-3 rounded-xl border-2 border-emerald-200 bg-emerald-50/40 hover:border-emerald-400 transition-all text-left"
            data-testid="link-account-transactions">
            <div className="p-2 rounded-lg shrink-0 bg-emerald-500 text-white"><FileSpreadsheet size={16} /></div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-emerald-900">Investor Account-wise Transactions</p>
              <p className="text-xs text-gray-500 mt-0.5">Bulk export of all transactions with date-range filter (since-inception default)</p>
            </div>
          </button>
          <button onClick={() => navigate('/investors/reports/incomplete-profiles')}
            className="flex items-start gap-3 p-3 rounded-xl border-2 border-rose-200 bg-rose-50/40 hover:border-rose-400 transition-all text-left"
            data-testid="link-incomplete-profiles">
            <div className="p-2 rounded-lg shrink-0 bg-rose-500 text-white"><ClipboardList size={16} /></div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-rose-900">Incomplete Investor Profiles</p>
              <p className="text-xs text-gray-500 mt-0.5">Accounts with missing applicable fields — fix gaps before audits and compliance reviews</p>
            </div>
          </button>
          <button onClick={() => navigate('/investors/reports/schedule-k1')}
            className="flex items-start gap-3 p-3 rounded-xl border-2 border-indigo-200 bg-indigo-50/40 hover:border-indigo-400 transition-all text-left"
            data-testid="link-schedule-k1">
            <div className="p-2 rounded-lg shrink-0 bg-indigo-500 text-white"><FileBadge size={16} /></div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-indigo-900">Schedule K-1 Investors</p>
              <p className="text-xs text-gray-500 mt-0.5">Accounts flagged &quot;Issue Schedule K-1&quot; under Compliance — customizable columns &amp; export</p>
            </div>
          </button>
        </div>
      </div>

      {/* Fund selector for fund_summary */}
      {activeReport === 'fund_summary' && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Select Fund</label>
          <select value={selectedFundId} onChange={e => setSelectedFundId(e.target.value)} className="border rounded-lg px-3 py-2 text-sm w-full max-w-xs" data-testid="fund-summary-fund-select">
            <option value="">-- Select a Fund --</option>
            {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
      )}

      {/* Fund-wise filter for Custom report */}
      {activeReport === 'custom' && (
        <div className="mb-4 bg-blue-50/40 border border-blue-100 rounded-lg p-3" data-testid="custom-fund-filter-wrap">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-blue-700 mb-1">Fund-wise Filter (optional)</label>
              <div className="flex items-center gap-2">
                <select value={customFundFilter} onChange={e => setCustomFundFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm flex-1 bg-white" data-testid="custom-fund-filter">
                  <option value="">All Funds (no filter)</option>
                  {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
                {customFundFilter && (
                  <button onClick={() => setCustomFundFilter('')} className="text-xs text-gray-500 hover:text-gray-700 underline" data-testid="clear-custom-fund-filter">Clear</button>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-700 mb-1">From Date (optional)</label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customFromDate}
                  onChange={e => setCustomFromDate(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm flex-1 bg-white"
                  data-testid="custom-from-date"
                  placeholder="Since inception"
                />
                {customFromDate && (
                  <button onClick={() => setCustomFromDate('')} className="text-xs text-gray-500 hover:text-gray-700 underline" data-testid="clear-custom-from-date">Clear</button>
                )}
              </div>
              <p className="text-[10px] text-blue-600/70 mt-1">Blank = since inception</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-700 mb-1">To Date (optional)</label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customToDate}
                  onChange={e => setCustomToDate(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm flex-1 bg-white"
                  data-testid="custom-to-date"
                  placeholder="As of today"
                />
                {customToDate && (
                  <button onClick={() => setCustomToDate('')} className="text-xs text-gray-500 hover:text-gray-700 underline" data-testid="clear-custom-to-date">Clear</button>
                )}
              </div>
              <p className="text-[10px] text-blue-600/70 mt-1">Blank = as of today</p>
            </div>
          </div>
          <p className="text-[11px] text-blue-600/70 mt-2">
            <span className="font-semibold">Fund-wise Filter</span> scopes accounts + Total Commitment / Contribution / Distribution / NAV to that fund.{' '}
            <span className="font-semibold">Date range</span> filters transaction-based totals: Capital Called, Contributed, Distribution, Pending Receipt, and NAV (as-of To Date). Commitment is a static snapshot and is NOT filtered.
          </p>
        </div>
      )}

      {/* Column Picker & Filter Panel (for all except AMC Consolidated) */}
      {showColumnPickerForReport && catalogData && (
        <div className="bg-white border rounded-xl mb-6" data-testid="report-config-panel">
          {/* Toolbar */}
          <div className="flex items-center justify-between px-5 py-3 border-b">
            <div className="flex items-center gap-3">
              <button onClick={() => setShowColumnPicker(p => !p)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${showColumnPicker ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                data-testid="toggle-column-picker">
                <Columns3 size={14} /> Columns ({selectedColumns.length})
              </button>
              {selectedColumns.length > 0 && (
                <button onClick={() => { setShowSaveDialog(true); if (!templateName) setTemplateName(''); }}
                  className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-600" data-testid="save-template-btn">
                  <Save size={14} /> Save
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {activeReport === 'custom' && (
                <button onClick={fetchReport} disabled={!selectedColumns.length || loading}
                  className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                  data-testid="generate-report-btn">
                  {loading ? 'Generating...' : 'Generate Report'}
                </button>
              )}
              <button onClick={handleExport} disabled={activeReport === 'amc_consolidated' ? !amcData : !data}
                className="flex items-center gap-1 px-4 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                data-testid="reports-export">
                <Download size={14} /> Export
              </button>
            </div>
          </div>

          {/* Saved templates */}
          {templates.length > 0 && showColumnPicker && (
            <div className="px-5 py-3 border-b bg-gray-50/50">
              <p className="text-xs text-gray-500 font-medium mb-2">Saved Templates</p>
              <div className="flex flex-wrap gap-2">
                {templates.map(tpl => (
                  <div key={tpl.id} className={`flex items-center gap-1 px-3 py-1 rounded-lg border text-sm cursor-pointer ${selectedTemplateId === tpl.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}>
                    <button onClick={() => loadTemplate(tpl)} className="font-medium" data-testid={`template-${tpl.id}`}>{tpl.name}</button>
                    <button onClick={() => deleteTemplate(tpl.id)} className="ml-1 text-gray-400 hover:text-red-500"><X size={12} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Column selector panel */}
          {showColumnPicker && (
            <div className="p-5 space-y-4 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">Select Columns ({selectedColumns.length} selected)</h3>
                <div className="flex gap-2">
                  {selectedColumns.length > 0 && <button onClick={() => setSelectedColumns([])} className="text-xs text-red-500 hover:text-red-700">Clear All</button>}
                  <button onClick={() => setSelectedColumns(DEFAULT_COLUMNS[activeReport] || [])} className="text-xs text-blue-600 hover:text-blue-800">Reset Defaults</button>
                </div>
              </div>
              <ColumnSelector catalog={activeCatalog} selected={selectedColumns} onToggle={toggleColumn} onToggleSection={toggleSection} />

              {/* Filters */}
              {selectedColumns.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1"><Filter size={14} /> Filters</h3>
                  <FilterPanel filterableFields={filterableFields} filters={filters} onFilterChange={setFilters} onRemoveFilter={removeFilter} onAddFilter={addFilter} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* AMC Consolidated toolbar */}
      {activeReport === 'amc_consolidated' && (
        <div className="flex items-center justify-between mb-4 gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" data-testid="reports-search" />
          </div>
          <button onClick={handleExport} disabled={!amcData} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50" data-testid="reports-export-amc">
            <Download size={16} /> Export Excel
          </button>
        </div>
      )}

      {/* Report content */}
      {activeReport === 'soa' ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="report-content">
          <SOAGeneration canEdit={true} />
        </div>
      ) : (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="report-content">
        {loading && <div className="py-12 text-center text-gray-400">Loading report...</div>}
        {!loading && activeReport === 'amc_consolidated' && renderAMCTable()}
        {!loading && activeReport !== 'amc_consolidated' && data && (
          <DynamicTable
            columns={data.columns || []}
            rows={data.rows || []}
            totals={data.totals}
            currency={activeReport === 'fund_summary' ? data.fund_currency : null}
          />
        )}
        {!loading && activeReport !== 'amc_consolidated' && !data && (
          <p className="text-gray-400 text-center py-8">
            {activeReport === 'custom' ? 'Select columns and click "Generate Report"' : 'Select columns to view the report'}
          </p>
        )}
      </div>
      )}

      {/* Save Template Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setShowSaveDialog(false)}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="save-template-dialog">
            <h3 className="text-lg font-semibold mb-4">{selectedTemplateId ? 'Update' : 'Save'} Report Template</h3>
            <input type="text" placeholder="Template name..." value={templateName} onChange={e => setTemplateName(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 mb-4 text-sm" autoFocus data-testid="template-name-input" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowSaveDialog(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
              <button onClick={saveTemplate} disabled={!templateName.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50" data-testid="confirm-save-template">
                {selectedTemplateId ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestorReports;
